import { createContext, useContext, useMemo, useState } from "react";
import { productLanguages, productTranslations } from "../lib/productTranslations";
import { safeGet, safeSet } from "../lib/safeStorage";

const supported = new Set(productLanguages.map(({ code }) => code));
const initialLocale = () => {
  const stored = safeGet("aramfix-locale");
  const pathLocale = window.location.pathname.split("/")[1]?.toLowerCase();
  const browser = (navigator.language || "en").split("-")[0].toLowerCase();
  return supported.has(pathLocale) ? pathLocale : (supported.has(stored) ? stored : (supported.has(browser) ? browser : "en"));
};
const LocaleContext = createContext(null);

export const LocaleProvider = ({ children }) => {
  const [locale, updateLocale] = useState(initialLocale);
  const setLocale = (value) => {
    const next = supported.has(value) ? value : "en";
    safeSet("aramfix-locale", next);
    document.documentElement.lang = next;
    const current = window.location.pathname.split("/")[1]?.toLowerCase();
    if (supported.has(current) && current !== next) {
      const suffix = window.location.pathname.split("/").slice(2).join("/");
      window.location.assign(`/${next}/${suffix}${window.location.search}${window.location.hash}`);
      return;
    }
    updateLocale(next);
  };
  const value = useMemo(() => ({
    locale,
    setLocale,
    languages: productLanguages,
    t: (key, variables = {}) => Object.entries(variables).reduce(
      (text, [name, replacement]) => text.replaceAll(`{${name}}`, String(replacement)),
      productTranslations[locale]?.[key] || productTranslations.en[key] || key,
    ),
    formatDate: (value, options = {}) => value ? new Intl.DateTimeFormat(locale, { dateStyle: "medium", ...options }).format(new Date(value)) : "",
    formatNumber: (value) => new Intl.NumberFormat(locale).format(Number(value || 0)),
  }), [locale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);